export const FEATURE_KEYS = {
  EXAM: "exam",
  ESSAY: "essay",
  RESOURCE: "resource",
  EDITAL: "edital",
  INSCRICAO: "enrollment",
} as const;

export const SUB_FEATURE_KEYS = {
  RESOURCE_QUESTION: "resource-question",
  RESOURCE_ESSAY: "resource-essay",
} as const;

export const ALL_FEATURE_KEYS = {
  ...FEATURE_KEYS,
  ...SUB_FEATURE_KEYS,
} as const;

export const FEATURE_ROUTES = {
  [FEATURE_KEYS.EXAM]: "/exam",
  [FEATURE_KEYS.ESSAY]: "/essay",
  [FEATURE_KEYS.RESOURCE]: "/resource",
  [FEATURE_KEYS.EDITAL]: "/edital",
  [FEATURE_KEYS.INSCRICAO]: "/enrollment",
} as const;

export const SUB_FEATURE_ROUTES = {
  [SUB_FEATURE_KEYS.RESOURCE_QUESTION]: "/resource/question",
  [SUB_FEATURE_KEYS.RESOURCE_ESSAY]: "/resource/essay",
} as const;

export const ALL_ROUTES = {
  ...FEATURE_ROUTES,
  ...SUB_FEATURE_ROUTES,
} as const;

export const ROUTE_PERMISSION_MAP: Record<
  string,
  { featureKey?: string; subFeatureKey?: string }
> = {
  [FEATURE_ROUTES[FEATURE_KEYS.EXAM]]: { featureKey: FEATURE_KEYS.EXAM },
  [FEATURE_ROUTES[FEATURE_KEYS.ESSAY]]: { featureKey: FEATURE_KEYS.ESSAY },
  [FEATURE_ROUTES[FEATURE_KEYS.RESOURCE]]: {
    featureKey: FEATURE_KEYS.RESOURCE,
  },
  [FEATURE_ROUTES[FEATURE_KEYS.EDITAL]]: { featureKey: FEATURE_KEYS.EDITAL },
  [FEATURE_ROUTES[FEATURE_KEYS.INSCRICAO]]: {
    featureKey: FEATURE_KEYS.INSCRICAO,
  },
  [SUB_FEATURE_ROUTES[SUB_FEATURE_KEYS.RESOURCE_QUESTION]]: {
    subFeatureKey: SUB_FEATURE_KEYS.RESOURCE_QUESTION,
  },
  [SUB_FEATURE_ROUTES[SUB_FEATURE_KEYS.RESOURCE_ESSAY]]: {
    subFeatureKey: SUB_FEATURE_KEYS.RESOURCE_ESSAY,
  },
} as const;

export const SUB_FEATURE_PARENT_MAP: Record<string, string> = {
  [SUB_FEATURE_KEYS.RESOURCE_QUESTION]: FEATURE_KEYS.RESOURCE,
  [SUB_FEATURE_KEYS.RESOURCE_ESSAY]: FEATURE_KEYS.RESOURCE,
} as const;

export const FEATURE_LABELS = {
  [FEATURE_KEYS.EXAM]: "Simulado",
  [FEATURE_KEYS.ESSAY]: "Redação",
  [FEATURE_KEYS.RESOURCE]: "Recurso",
  [FEATURE_KEYS.EDITAL]: "Edital",
  [FEATURE_KEYS.INSCRICAO]: "Visualizar Inscrição",
} as const;

export const SUB_FEATURE_LABELS = {
  [SUB_FEATURE_KEYS.RESOURCE_QUESTION]: "Recurso - Questão",
  [SUB_FEATURE_KEYS.RESOURCE_ESSAY]: "Recurso - Redação",
} as const;

export const ALL_LABELS = {
  ...FEATURE_LABELS,
  ...SUB_FEATURE_LABELS,
} as const;

export const FEATURE_DESCRIPTIONS = {
  [FEATURE_KEYS.EXAM]: "Confira sua classificação e notas.",
  [FEATURE_KEYS.ESSAY]: "Acesse a redação e veja suas redações e comentários.",
  [FEATURE_KEYS.RESOURCE]: "Solicite a revisão de alguma questão ou resultado.",
  [FEATURE_KEYS.EDITAL]: "Acesse os detalhes e regras do processo seletivo.",
  [FEATURE_KEYS.INSCRICAO]: "Acesse os detalhes da sua inscrição.",
} as const;

export const SUB_FEATURE_DESCRIPTIONS = {
  [SUB_FEATURE_KEYS.RESOURCE_QUESTION]:
    "Solicite recurso para questões de um simulado.",
  [SUB_FEATURE_KEYS.RESOURCE_ESSAY]:
    "Solicite recurso para redação ou prova discursiva.",
} as const;

export const ALL_DESCRIPTIONS = {
  ...FEATURE_DESCRIPTIONS,
  ...SUB_FEATURE_DESCRIPTIONS,
} as const;

export const isMainFeature = (
  key: string
): key is keyof typeof FEATURE_KEYS => {
  return Object.values(FEATURE_KEYS).includes(key as any);
};

export const isSubFeature = (
  key: string
): key is keyof typeof SUB_FEATURE_KEYS => {
  return Object.values(SUB_FEATURE_KEYS).includes(key as any);
};

export const getParentFeature = (subFeatureKey: string): string | undefined => {
  return SUB_FEATURE_PARENT_MAP[subFeatureKey];
};

export const getRoutePath = (key: string): string | undefined => {
  return ALL_ROUTES[key as keyof typeof ALL_ROUTES];
};

export const getLabel = (key: string): string | undefined => {
  return ALL_LABELS[key as keyof typeof ALL_LABELS];
};

export const getDescription = (key: string): string | undefined => {
  return ALL_DESCRIPTIONS[key as keyof typeof ALL_DESCRIPTIONS];
};

export type MainFeatureKey = (typeof FEATURE_KEYS)[keyof typeof FEATURE_KEYS];

export type SubFeatureKey =
  (typeof SUB_FEATURE_KEYS)[keyof typeof SUB_FEATURE_KEYS];

export type FeatureKey = MainFeatureKey | SubFeatureKey;

export type RoutePath = (typeof ALL_ROUTES)[keyof typeof ALL_ROUTES];

export type PermissionConfig = {
  featureKey?: MainFeatureKey;
  subFeatureKey?: SubFeatureKey;
};
